import { CheckIcon, HighlighterIcon, PlayIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { memo, type ReactNode } from "react";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../basic/phosphorIcon/PhosphorIcon";
import { ClassActivityCategory } from "../../constants/ClassActivityConstants";
import { ShareModes } from "../../enums/shareFlow";
import { getDateTimeFromMilliseconds, getReadableDurationFromTimeLimit } from "../../helpers/DateTime";
import { ClassMemberActivityStatuses } from "../../helpers/ClassMemberActivities";
import useBreakpoints from "../../hooks/useBreakpoints";
import { AreaButton, ButtonVariant, IconPosition } from "../teacherArea/AreaButton";
import { ActivityMetaRow } from "./ActivityMetaRow";
import { ORANGE_OUTLINE_PILL_BUTTON_CLASS } from "./classMemberActivityButtonStyles";
import { ClassMemberActivityCardShell } from "./ClassMemberActivityCardShell";

interface ClassMemberActivityCardProps {
    activityId: string;
    shareMode: ShareModes;
    status: ClassMemberActivityStatuses;
    name: string;
    timeLimit?: string;
    dueDate?: number;
    onStart?: () => void;
    onViewResult?: () => void;
}

const ClassMemberActivityCard = (props: ClassMemberActivityCardProps) => {
    const t = useT();
    const { isAboveMD } = useBreakpoints();

    const isPractice = props.shareMode === ShareModes.PRACTICE;
    const isNew = props.status === ClassMemberActivityStatuses.NEW;
    const isSubmitted = props.status === ClassMemberActivityStatuses.SUBMITTED;
    const isEvaluated = props.status === ClassMemberActivityStatuses.EVALUATED;

    // Custom key: the keyless "Test" string translates to "Aufgabe" (phase6's feature name), but
    // share-mode labels must read "Test" in German too. The key must be an inline literal (the
    // Transifex CLI extractor can't resolve constants) and identical at every share-mode label.
    const t_testCategory = t("Test", {
        _key: "test.mode.label",
        _tags: "ClassMemberDashboard, ClassMemberActivityCard",
    });
    const t_practiceCategory = t("Practice", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_start = t("Start now", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_practice = t("Practice now", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_viewResult = t("View result", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_submitted = t("Submitted", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_evaluating = t("Your teacher is evaluating your answers.", {
        _tags: "ClassMemberDashboard, ClassMemberActivityCard",
    });
    const t_finishedEvaluating = t("Finished evaluating", {
        _tags: "ClassMemberDashboard, ClassMemberActivityCard",
    });
    const t_until = t("Until {date}", {
        _tags: "ClassMemberDashboard, ClassMemberActivityCard",
        date: props.dueDate ? getDateTimeFromMilliseconds(props.dueDate) : "",
    });
    const t_hour = t("Hour", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_hours = t("Hours", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_minute = t("Minute", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });
    const t_minutes = t("Minutes", { _tags: "ClassMemberDashboard, ClassMemberActivityCard" });

    const renderBadgeIcon = (iconSize: string) => {
        // Practice and evaluated tests get a Phosphor badge icon; new/submitted tests get the
        // phase-six annotation icon.
        if (isPractice || isEvaluated) {
            return (
                <PhosphorIcon
                    phosphorIcon={isPractice ? HighlighterIcon : CheckIcon}
                    fontSize={iconSize}
                    color="text-p6-orange"
                />
            );
        }
        return (
            <PhaseSixIcon
                name="annotation-alt"
                className={`shrink-0 ${iconSize} text-p6-orange`}
            />
        );
    };

    // Test "new" meta: time limit • Until <deadline>. Both optional — only rendered when set.
    const renderMetaRow = () => {
        if (isPractice || !isNew) return null;
        const parts: string[] = [];
        if (props.timeLimit) {
            parts.push(
                getReadableDurationFromTimeLimit(props.timeLimit, {
                    hour: t_hour,
                    hours: t_hours,
                    minute: t_minute,
                    minutes: t_minutes,
                })
            );
        }
        if (props.dueDate) parts.push(t_until);
        if (parts.length === 0) return null;
        return <ActivityMetaRow parts={parts} />;
    };

    // Status line shown below the name for submitted/evaluated tests.
    const renderStatusLine = () => {
        if (isSubmitted) {
            return <p className="text-l text-p6-grey-dark break-words">{t_evaluating}</p>;
        }
        if (isEvaluated) {
            return (
                <div className="flex items-center gap-xs text-l text-p6-grey-dark">
                    <PhosphorIcon
                        phosphorIcon={CheckIcon}
                        fontSize="text-l"
                        color="text-p6-grey-dark"
                    />
                    <span>{t_finishedEvaluating}</span>
                </div>
            );
        }
        return null;
    };

    const renderSubInfo = (): ReactNode => renderMetaRow() ?? renderStatusLine();

    const renderAction = () => {
        if (isSubmitted) {
            // Desktop stacks icon over label with extra breathing room to the card edge;
            // mobile puts them side by side.
            return (
                <div className={`flex items-center ${isAboveMD ? "flex-col gap-xxs mr-xl" : "flex-row gap-xs"}`}>
                    <PhosphorIcon
                        phosphorIcon={CheckIcon}
                        fontSize="text-[32px]"
                        color="text-p6-orange"
                    />
                    <span className="text-m text-p6-orange-darker">{t_submitted}</span>
                </div>
            );
        }
        if (isEvaluated) {
            return (
                <AreaButton
                    variant={ButtonVariant.SECONDARY}
                    role="button"
                    onClick={props.onViewResult}
                    className={ORANGE_OUTLINE_PILL_BUTTON_CLASS}
                >
                    {t_viewResult}
                </AreaButton>
            );
        }
        return (
            <AreaButton
                variant={ButtonVariant.SECONDARY}
                role="button"
                onClick={props.onStart}
                data-testid="classroom-activity-start"
                phosphorIcon={PlayIcon}
                iconPosition={IconPosition.RIGHT}
                className={ORANGE_OUTLINE_PILL_BUTTON_CLASS}
            >
                {isPractice ? t_practice : t_start}
            </AreaButton>
        );
    };

    return (
        <ClassMemberActivityCardShell
            category={ClassActivityCategory.TEST}
            renderBadgeIcon={renderBadgeIcon}
            categoryLabel={isPractice ? t_practiceCategory : t_testCategory}
            name={props.name}
            subInfo={renderSubInfo()}
            action={renderAction()}
            isActionCentered={isSubmitted}
            data-testid={`classroom-activity-${props.activityId}`}
            data-status={props.status}
        />
    );
};

export default memo(ClassMemberActivityCard);
